import isEmpty from 'lodash/isEmpty'
import type { HaulerTemplateDefinition } from './HaulerTemplateDefinition'

/**
 * Parses a raw JSON string, tolerating a missing/empty value or malformed JSON — never
 * throws. Non-string input (e.g. a value the caller already JSON-parsed, as
 * `IAppSettingService.getTenantSetting` normally returns) is passed through unchanged,
 * matching `@ztechapps/common.utils`'s `jsonTryParse`. Kept local here (rather than
 * importing that helper) so this file stays resolvable by the mobile app's isolated
 * `tsc`, which has no module mapping for that package (Metro-only resolution).
 */
const safeParse = (raw: unknown): unknown => {
  if (typeof raw !== 'string') return raw
  try {
    return JSON.parse(raw)
  } catch {
    return undefined
  }
}

/**
 * Resolves a hauler's single template snapshot straight from the raw `hauler.template`
 * AppSetting value. Returns `undefined` when `raw` is missing, empty, malformed JSON, or not
 * shaped like a template (no `fields` array) — the caller supplies its own default
 * (`?? MILK_TEMPLATE`) in that case. `code` is NOT required: a template without a code is
 * still a valid definition — field-layout resolution falls back to `DEFAULT_LAYOUT` when
 * `code` is absent, it isn't load-bearing here.
 */
export const resolveHaulerTemplateValue = (raw?: string): HaulerTemplateDefinition | undefined => {
  if (isEmpty(raw)) return undefined
  const parsed = safeParse(raw) as HaulerTemplateDefinition | undefined
  if (!parsed || !Array.isArray(parsed.fields)) return undefined
  return parsed
}
